import React, { useEffect, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  RowData,
  useReactTable
} from '@tanstack/react-table';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';

import { Icon } from '@iconify/react';
import './../../ratecard/RateCard.scss';
import CustomTimePicker from '../../../../../../../common/components/customized/customtimepicker/CustomTimePicker';
import CustomButton from '../../../../../../../common/components/buttons/CustomButton';
import CustomTextField from '../../../../../../../common/components/customized/customtextfield/CustomTextField';
import CustomIconButton from '../../../../../../../common/components/buttons/CustomIconButton';
import { CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import ToolTip from '../../../../../../../common/pages/user/management/components/reports/ToolTip';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateData: (rowIndex: number, columnId: string, value: unknown) => void;
  }
}

type EditableTableProps = {
  control: any;
  columns: any;
  data: any;
  updateTour: any;
  modeIndex: any;
  modeFields: any;
  getValues?: any;
  loading?: any;
  trigger?: any;
  setValue?: any;
  setError?: any;
  clearErrors?: any;
  setEditingRow: any;
  editingRow: any;
};

type AddOn = {
  cost: string;
  startTime: Date | null;
  endTime: Date | null;
};

interface AddOnProps {
  rowId: number | null;
  columnId: number | null;
}

export function EditableTable({
  control,
  columns,
  data,
  updateTour,
  modeIndex,
  modeFields,
  getValues,
  loading,
  trigger,
  setValue,
  setError,
  clearErrors,
  setEditingRow,
  editingRow
}: EditableTableProps) {
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [isaddOnIndex, setIsAddOnIndex] = useState<any>({ rowId: null, columnId: null });
  const [addOnChanges, setAddOnChanges] = useState<any>({});

  const finalColumns: any = [
    ...columns,
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }: any) =>
        editingRow === row.index ? (
          <div style={{ display: 'flex', gap: '2px' }}>
            <Icon
              icon='hugeicons:tick-01'
              width='22'
              height='22'
              onClick={() => saveEdit(row.index)}
              style={{
                cursor: 'pointer',
                color: '#16a34a',
                marginRight: '10px',
                border: '1px solid',
                borderRadius: '50%'
              }}
            />

            <Icon
              icon='ix:cancel'
              width='20'
              height='20'
              onClick={() => cancelEdit(row.index)}
              style={{
                cursor: 'pointer',
                color: 'rgb(163 22 22)',
                border: '1px solid',
                borderRadius: '50%'
              }}
            />
            {/* </IconButton> */}
          </div>
        ) : (
          <IconButton onClick={() => startEdit(row.index)}>
            <Icon
              icon='mdi:edit-outline'
              width='24'
              height='24'
              style={{ cursor: 'pointer', color: '#abab1c' }}
            />
          </IconButton>
        )
    }
  ];

  const startEdit = (rowIndex: number) => {
    updateTour(modeIndex, {
      ...modeFields[modeIndex],
      tours: modeFields[modeIndex].tours?.map((tour: any, tourIndex: number) => ({
        ...tour,
        seaters:
          rowIndex !== tourIndex
            ? tour?.seaters?.map((seater: any) => ({
                ...seater,
                baseCost: seater?.currentBaseCost
              }))
            : tour.seaters
      }))
    });
    setEditingRow(rowIndex);
  };

  const cancelEdit = (rowIndex: number) => {
    updateTour(modeIndex, {
      ...modeFields[modeIndex],
      tours: modeFields[modeIndex]?.tours?.map((item: any, tourIndex: number) => ({
        ...item,
        seaters:
          rowIndex === tourIndex
            ? item?.seaters?.map((value: any) => ({
                ...value,
                baseCost: value?.currentBaseCost || 0
              }))
            : item?.seaters
      }))
    });
    setEditingRow(null);
  };

  const saveEdit = (index: any) => {
    updateTour(modeIndex, {
      ...modeFields[modeIndex],
      tours: modeFields[modeIndex]?.tours?.map((item: any, tourIndex: number) => ({
        ...item,
        seaters:
          index === tourIndex
            ? item?.seaters?.map((value: any) => ({
                ...value,
                baseCost: value?.baseCost ? Number(value?.baseCost) : 0,
                currentBaseCost: Number(value?.baseCost || 0) ?? 0
              }))
            : item?.seaters
      }))
    });
    setEditingRow(null);
  };

  const handleDialogClose = () => {
    const seaterIndex = data[isaddOnIndex.rowId]?.seaters?.findIndex(
      (s: any) => s.seater.toString() === isaddOnIndex.columnId
    );
    updateTour(modeIndex, {
      ...modeFields[modeIndex],
      tours: modeFields[modeIndex]?.tours?.map((item: any, tourIndex: number) => ({
        ...item,
        seaters:
          isaddOnIndex.rowId === tourIndex
            ? item?.seaters?.map((seater: any, sIndex: number) => ({
                ...seater,
                addOns: sIndex === seaterIndex ? seater.prevaddOns : seater?.addOns
              }))
            : item?.seaters
      }))
    });
    clearErrors(
      `modes.${modeIndex}.tours.${
        isaddOnIndex.rowId
      }.seaters.${seaterIndex}.addOns.${0}.endTime`
    );
    setIsOpenDialog(false);
  };

  const handleAddOnSubmit = () => {
    const seaterIndex = data[isaddOnIndex.rowId]?.seaters?.findIndex(
      (s: any) => s.seater.toString() === isaddOnIndex.columnId
    );

    if (seaterIndex === -1) return;

    const formValues = control._formValues || control._defaultValues;
    const currentAddOns =
      formValues?.modes?.[modeIndex]?.tours?.[isaddOnIndex.rowId]?.seaters?.[seaterIndex]
        ?.addOns || [];

    const processedAddOns = currentAddOns.map((addOn: any) => ({
      ...addOn,
      startTime: addOn.startTime ? addOn.startTime : null,
      endTime: addOn.endTime ? addOn.endTime : null
    }));

    if (processedAddOns[0]?.endTime > processedAddOns[0]?.startTime) {
      updateTour(modeIndex, {
        ...modeFields[modeIndex],
        tours: modeFields[modeIndex]?.tours?.map((item: any, tourIndex: number) => ({
          ...item,
          seaters:
            isaddOnIndex.rowId === tourIndex
              ? item?.seaters?.map((seater: any, sIndex: number) => ({
                  ...seater,
                  addOns: sIndex === seaterIndex ? processedAddOns : seater?.addOns,
                  prevaddOns: sIndex === seaterIndex ? processedAddOns : seater?.addOns
                }))
              : item?.seaters
        }))
      });
      setIsOpenDialog(false);
      setIsAddOnIndex({ rowId: null, columnId: null });
      setAddOnChanges({});
    } else {
      updateTour(modeIndex, {
        ...modeFields[modeIndex],
        tours: modeFields[modeIndex]?.tours?.map((item: any, tourIndex: number) => ({
          ...item,
          seaters:
            isaddOnIndex.rowId === tourIndex
              ? item?.seaters?.map((seater: any, sIndex: number) => ({
                  ...seater,
                  addOns: sIndex === seaterIndex ? processedAddOns : seater?.addOns,
                  prevaddOns: [{ name: '', addCost: 0, startTime: null, endTime: null }]
                }))
              : item?.seaters
        }))
      });
      setError(
        `modes.${modeIndex}.tours.${
          isaddOnIndex.rowId
        }.seaters.${seaterIndex}.addOns.${0}.endTime`,
        { type: 'custom', message: 'Invalid end time' }
      );
    }
  };

  const updateSeatersData = (
    cell: any,
    rowIndex: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateTour(modeIndex, {
      ...modeFields[modeIndex],
      tours: modeFields[modeIndex]?.tours?.map((item: any, tourIndex: number) => ({
        ...item,
        seaters:
          rowIndex === tourIndex
            ? item?.seaters?.map((value: any) => ({
                ...value
              }))
            : item?.seaters
      }))
    });
  };

  const table = useReactTable({
    data: data ?? [],
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnPinning: true,
    initialState: {
      columnPinning: { left: ['tourName'] }
    }
  });
  useEffect(() => {
    setEditingRow(null);
  }, [modeIndex]);

  return (
    <>
      <div
        style={{
          overflowX: table?.getAllLeafColumns()?.length > 5 ? 'auto' : 'visible',
          maxWidth: table?.getAllLeafColumns()?.length > 5 ? '1280px' : '100%'
        }}
      >
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px'
            }}
          >
            <CircularProgress size={32} thickness={4} />
          </div>
        ) : data?.length > 0 ? (
          <table style={{ borderCollapse: 'collapse' }}>
            <thead>
              {table?.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    const isFirst = header.column.id === 'tourName';
                    const isLast = header.column.id === 'actions';

                    return (
                      <th
                        key={header.id}
                        style={{
                          minWidth: isLast ? '60px' : '13rem',
                          backgroundColor: '#fff',
                          padding: '8px',
                          border: '1px solid #d4d7db',
                          height: '5vh',
                          position: isFirst || isLast ? 'sticky' : 'static',
                          left: isFirst ? 0 : undefined,
                          right: isLast ? 0 : undefined,
                          zIndex: isFirst || isLast ? 2 : 1
                        }}
                      >
                        <span>{String(header.column.columnDef.header)}</span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table?.getRowModel().rows.map((row, rowindex) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell: any, cellIndex) => {
                    const isFirst = cellIndex === 0;
                    const isLast = cellIndex === row?.getVisibleCells()?.length - 1;
                    const hasAddOns = 0;

                    return (
                      <td
                        key={cell.id}
                        style={{
                          minWidth: isLast ? '60px' : '13rem',
                          padding: '10px',
                          border: '2px solid #80808033',
                          textAlign: 'center',
                          fontSize: isFirst ? '12px' : 'none',
                          color: isFirst ? 'black' : '#16a34a',
                          position: isFirst || isLast ? 'sticky' : 'static',
                          left: isFirst ? 0 : undefined,
                          right: isLast ? 0 : undefined,
                          background: 'white',
                          zIndex: isFirst || isLast ? 1 : 0
                        }}
                      >
                        {[isLast, isFirst]?.includes(true) ? (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        ) : editingRow === row.index && !isFirst ? (
                          <CustomTextField
                            key={cell.id}
                            control={control}
                            name={`modes.${modeIndex}.tours.${row?.index}.seaters.${
                              cellIndex - 1
                            }.baseCost`}
                            placeholder='Base Cost'
                            id={`modes.${modeIndex}.tours.${row?.index}.seaters.${
                              cellIndex - 1
                            }.baseCost`}
                            type={'number'}
                            onChangeCallback={(event: any) => {
                              updateSeatersData(cell, row.index, event);
                            }}
                            isDecimal={true}
                          />
                        ) : (
                          <></>
                        )}

                        {![isFirst, isLast, editingRow === row.index]?.some(Boolean) && (
                          <div className='add-ons-parent'>
                            {Boolean(
                              getValues(
                                `modes.${modeIndex}.tours.${row?.index}.seaters.${
                                  cellIndex - 1
                                }.baseCost`
                              )
                            ) && (
                              <Tooltip title='Add-on'>
                                <Icon
                                  icon='mdi:edit-circle'
                                  width='24'
                                  height='24'
                                  style={{
                                    cursor: 'pointer',
                                    color: 'rgb(163 146 22)',
                                    position: 'absolute',
                                    top: '-8px',
                                    right: '-4px',
                                    backgroundColor: 'white'
                                  }}
                                  onClick={() => {
                                    setIsOpenDialog(true);
                                    setIsAddOnIndex({
                                      rowId: row?.index,
                                      columnId: cell?.column?.id
                                    });
                                  }}
                                />
                              </Tooltip>
                            )}

                            <span>
                              {' '}
                              {getValues(
                                `modes.${modeIndex}.tours.${row?.index}.seaters.${
                                  cellIndex - 1
                                }.baseCost`
                              )}
                            </span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px',
              fontSize: '16px',
              fontWeight: 500,
              color: '#666',
              height: '100%',
              // backgroundColor: '#f9f9f9',
              borderRadius: '8px'
            }}
          >
            No data found
          </div>
        )}
      </div>

      {/* Add-ons Dialog */}
      {isOpenDialog && (
        <Dialog open={true} maxWidth='md' fullWidth>
          <DialogContent>
            <Typography variant='h5' gutterBottom>
              Add-on for Seater{' '}
              {(() => {
                const seater = data[isaddOnIndex.rowId]?.seaters?.find(
                  (s: any) => s.seater.toString() === isaddOnIndex.columnId
                );
                return seater?.seater;
              })()}{' '}
              - {data[isaddOnIndex.rowId]?.tourName}
            </Typography>

            <Box
              className='addratecard-close'
              sx={{ position: 'absolute', top: 10, right: 10, zIndex: 3 }}
            >
              <Icon
                icon='ic:round-close'
                className='ratecard-close-icon'
                onClick={handleDialogClose}
              />
            </Box>

            <Grid container spacing={1}>
              {(() => {
                const seater = data[isaddOnIndex.rowId]?.seaters?.find(
                  (s: any) => s.seater.toString() === isaddOnIndex.columnId
                );

                const seaterIndex = data[isaddOnIndex.rowId]?.seaters?.findIndex(
                  (s: any) => s.seater.toString() === isaddOnIndex.columnId
                );

                if (!seater || seaterIndex === -1) return null;

                return seater.addOns?.map((addOn: any, addOnIndex: number) => {
                  const changeKey = `${isaddOnIndex.rowId}-${isaddOnIndex.columnId}-${addOnIndex}`;
                  const changes = addOnChanges[changeKey] || {};

                  return (
                    <Grid item xs={12} key={addOnIndex}>
                      {/* <Typography variant='subtitle2'>Add-on {addOnIndex + 1}</Typography> */}
                      <Grid container spacing={1}>
                        <Grid item xs={3}>
                          <CustomTextField
                            id={`${addOnIndex}.name`}
                            label='Add-on Name'
                            name={`modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.name`}
                            control={control}
                            placeholder='Add-on name'
                            isOptional
                            onChangeCallback={(e: any) => {
                              setValue(
                                `modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.name`,
                                e
                              );
                            }}
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <CustomTextField
                            id={addOnIndex.toString()}
                            label='Add-on Cost'
                            name={`modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.addCost`}
                            control={control}
                            placeholder='Cost'
                            type='number'
                            isOptional
                            isDecimal={true}
                            onChangeCallback={(e: any) => {
                              setValue(
                                `modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.addCost`,
                                e,
                                { shouldValidate: true }
                              );
                              // if (e === 0) {
                              //   setValue(
                              //     `modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.startTime`,
                              //     null
                              //   );
                              //   setValue(
                              //     `modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.endTime`,
                              //     null
                              //   );
                              // }
                            }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <CustomTimePicker
                            id={`${addOnIndex}.startTime`}
                            name={`modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.startTime`}
                            control={control}
                            label='Start Time'
                            isClock={true}
                            isOptional={true}
                            isDisabled={
                              getValues(
                                `modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.addCost`
                              ) > 0
                                ? false
                                : true
                            }
                            values={addOn?.startTime ? addOn?.startTime : null}
                            // onTimeChange={(e: any) => {
                            //   setValue(
                            //     `modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.startTime`,
                            //     e?.$d
                            //   );
                            // }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: 35
                              },
                              '& .MuiInputBase-input': {
                                padding: '10px 12px',
                                fontSize: '14px'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <CustomTimePicker
                            id={`${addOnIndex}.endTime`}
                            name={`modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.endTime`}
                            control={control}
                            label='End Time'
                            isOptional={true}
                            isDisabled={
                              getValues(
                                `modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.addCost`
                              ) > 0
                                ? false
                                : true
                            }
                            values={addOn?.endTime ? addOn.endTime : null}
                            // onTimeChange={(e: any) => {
                            //   setValue(
                            //     `modes.${modeIndex}.tours.${isaddOnIndex.rowId}.seaters.${seaterIndex}.addOns.${addOnIndex}.endTime`,
                            //     e?.$d
                            //   );
                            // }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: 35
                              },
                              '& .MuiInputBase-input': {
                                padding: '10px 12px',
                                fontSize: '14px'
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                });
              })()}
            </Grid>
          </DialogContent>

          <DialogActions sx={{ padding: '16px 24px' }}>
            <CustomButton
              category='Cancel'
              className='cancel'
              onClick={handleDialogClose}
            />
            <CustomButton
              category='Save'
              type='submit'
              className='saveChanges'
              onClick={handleAddOnSubmit}
            />
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
